import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';

// ─── Types ────────────────────────────────────────────────────────────────────

type ActivityType = 'CALL' | 'VOICEMAIL' | 'LETTER' | 'EMAIL' | 'NOTE';

interface TimeEntry {
  entry_id: string;
  claim_id: string;
  handler_id: string;
  handler_name: string;
  hours: number;
  activity_type: ActivityType;
  notes: string;
  created_at: string;
  claim_reference: string;
}

interface Props {
  claimId: string;
  claimReference?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const HOUR_PRESETS = [0.1, 0.2, 0.3, 0.5, 1.0];

const ACTIVITY_OPTIONS: { value: ActivityType; label: string }[] = [
  { value: 'CALL', label: 'Call' },
  { value: 'VOICEMAIL', label: 'Voicemail' },
  { value: 'LETTER', label: 'Letter' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'NOTE', label: 'Note' },
];

const ACTIVITY_ICONS: Record<ActivityType, string> = {
  CALL: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
  VOICEMAIL: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  LETTER: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  EMAIL: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  NOTE: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(value: string): string {
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTime(value: string): string {
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function TimeTab({ claimId, claimReference }: Props) {
  // Entry form state
  const [selectedHours, setSelectedHours] = useState<number | null>(null);
  const [customHours, setCustomHours] = useState('');
  const [activityType, setActivityType] = useState<ActivityType>('CALL');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Entries list state
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [totalHours, setTotalHours] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const effectiveHours = selectedHours ?? (customHours ? parseFloat(customHours) : null);

  const fetchEntries = useCallback(async () => {
    try {
      const result = await (api as any).getClaimTime(claimId);
      setEntries(result.items || []);
      setTotalHours(result.total_hours || 0);
      setLoadError(null);
    } catch (err: any) {
      setLoadError(err.message || 'Failed to load time entries.');
    } finally {
      setLoading(false);
    }
  }, [claimId]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  async function handleSubmit() {
    if (!effectiveHours || effectiveHours <= 0) return;

    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      const result = await (api as any).addTimeEntry({
        claim_id: claimId,
        hours: effectiveHours,
        activity_type: activityType,
        notes: notes.trim() || undefined,
        claim_reference: claimReference || undefined,
      });

      // Optimistic update — prepend new entry to list
      setEntries((prev) => [result, ...prev]);
      setTotalHours((prev) => Math.round((prev + effectiveHours) * 100) / 100);

      // Reset form
      setSelectedHours(null);
      setCustomHours('');
      setNotes('');
      setActivityType('CALL');
      setSubmitSuccess(true);
      setTimeout(() => setSubmitSuccess(false), 2000);
    } catch (err: any) {
      setSubmitError(err.message || 'Failed to log time.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6">

      {/* ── Quick Entry Form ────────────────────────────────────── */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 space-y-4">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Log Time</h3>

        {/* Hours selection */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-2">Hours</label>
          <div className="flex flex-wrap items-center gap-2">
            {HOUR_PRESETS.map((h) => (
              <button
                key={h}
                onClick={() => { setSelectedHours(h); setCustomHours(''); }}
                disabled={submitting}
                className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                  selectedHours === h
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                } disabled:opacity-50`}
              >
                {h}
              </button>
            ))}
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                step="0.1"
                min="0.1"
                max="24"
                value={customHours}
                onChange={(e) => {
                  setCustomHours(e.target.value);
                  setSelectedHours(null);
                }}
                placeholder="Custom"
                disabled={submitting}
                className="w-20 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
              <span className="text-xs text-gray-400">hrs</span>
            </div>
          </div>
        </div>

        {/* Activity type */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-2">Activity</label>
          <div className="flex flex-wrap gap-2">
            {ACTIVITY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setActivityType(opt.value)}
                disabled={submitting}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
                  activityType === opt.value
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                } disabled:opacity-50`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ACTIVITY_ICONS[opt.value]} />
                </svg>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Notes <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Brief description of activity..."
            disabled={submitting}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          />
        </div>

        {/* Submit */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleSubmit}
            disabled={submitting || !effectiveHours || effectiveHours <= 0}
            className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {submitting ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Logging...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6l4 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Log Time
              </>
            )}
          </button>

          {submitSuccess && (
            <span className="inline-flex items-center gap-1 text-sm text-green-600 font-medium">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Logged
            </span>
          )}
        </div>

        {submitError && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <p className="text-xs text-red-700">{submitError}</p>
          </div>
        )}
      </div>

      {/* ── Entries List ─────────────────────────────────────────── */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            Recent Time Entries
          </h3>
          {!loading && entries.length > 0 && (
            <span className="text-sm font-medium text-gray-600">
              Total: <span className="text-blue-700 font-bold">{totalHours}h</span>
            </span>
          )}
        </div>

        {loading && (
          <div className="flex items-center gap-2 py-8 justify-center">
            <svg className="animate-spin w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <span className="text-sm text-gray-500">Loading time entries...</span>
          </div>
        )}

        {!loading && loadError && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
            <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <p className="text-sm text-red-700">{loadError}</p>
          </div>
        )}

        {!loading && !loadError && entries.length === 0 && (
          <p className="text-sm text-gray-500 py-6 text-center">No time entries for this claim yet.</p>
        )}

        {!loading && !loadError && entries.length > 0 && (
          <div className="divide-y divide-gray-100">
            {entries.map((entry) => (
              <div key={entry.entry_id} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                {/* Hours badge */}
                <span className="inline-flex items-center justify-center w-12 h-8 rounded-lg bg-blue-50 text-blue-700 text-sm font-bold tabular-nums flex-shrink-0">
                  {entry.hours}h
                </span>

                {/* Activity icon + type */}
                <div className="flex items-center gap-1.5 min-w-[90px]">
                  <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ACTIVITY_ICONS[entry.activity_type] || ACTIVITY_ICONS.NOTE} />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">
                    {ACTIVITY_OPTIONS.find((o) => o.value === entry.activity_type)?.label || entry.activity_type}
                  </span>
                </div>

                {/* Date */}
                <span className="text-xs text-gray-400 whitespace-nowrap min-w-[90px]">
                  {formatDate(entry.created_at)}
                  <span className="block text-[10px]">{formatTime(entry.created_at)}</span>
                </span>

                {/* Handler */}
                <span className="text-xs text-gray-500 whitespace-nowrap min-w-[80px]">
                  {entry.handler_name?.split(' ').map((n, i) => i === 0 ? n : `${n[0]}.`).join(' ') || '—'}
                </span>

                {/* Notes */}
                <span className="text-xs text-gray-500 flex-1 truncate">
                  {entry.notes || '—'}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Total bar */}
        {!loading && !loadError && entries.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
            <span className="text-xs text-gray-500">{entries.length} {entries.length === 1 ? 'entry' : 'entries'}</span>
            <span className="text-sm font-semibold text-gray-700">
              Total logged: <span className="text-blue-700">{totalHours}h</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
